import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { FleetrifyModal } from "../Modal/modal";
import {
  useGetBanksQuery,
  useGetBeneficiariesQuery,
  useAddBeneficiaryMutation,
  useVerifyAccountMutation,
  useDeleteBeneficiaryMutation,
} from "../../redux/services/Wallet";
import BankDropdown from "./BankDropdown";
import { toast } from "sonner";
interface Beneficiary {
  id: number;
  name: string;
  bankName: string;
  accountNumber: string;
  bankCode?: string;
}
type Bank = {
  name: string;
  code: string;
};

const ManagePayoutAccounts = () => {
  const { data: beneficiariesData, isLoading: loadingBeneficiaries } =
    useGetBeneficiariesQuery();
  const { data: banks = [], isLoading, isError } = useGetBanksQuery();

  // Use data from API, fallback to an empty array if undefined
  const savedBeneficiaries = beneficiariesData?.data ?? [];
 const [addBeneficiary] = useAddBeneficiaryMutation();
 const [
     verifyAccount,
     { data: verifyData, isLoading: verifying, error: verifyError },
   ] = useVerifyAccountMutation();
   const [deleteBeneficiary, { isLoading: deleting }] =
     useDeleteBeneficiaryMutation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Beneficiary | null>(
    null
  );

  const [formData, setFormData] = useState<{
    bank: Bank | null;
    accountNumber: string;
    receiversName: string;
  }>({
    bank: null,
    accountNumber: "",
    receiversName: "",
  });

  const [errors, setErrors] = useState({
    bank: "",
    accountNumber: "",
  });

  const handleDeleteClick = (account: any) => {
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedAccount) {
      try {
        await deleteBeneficiary(String(selectedAccount.id)).unwrap();
        toast.success("Beneficiary deleted successfully");
        setSelectedAccount(null);
        setShowDeleteModal(false);
      } catch (err: any) {
        console.error("❌ Failed to delete beneficiary:", err);
        toast.error(
          err?.data?.error?.message || "Failed to delete beneficiary"
        );
      }
    }
  };


  const handleBlur = (field: string) => {
    if (!formData[field as keyof typeof formData]) {
      setErrors((prev) => ({ ...prev, [field]: "This field is required" }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const isFormValid = () => formData.bank && formData.accountNumber;
const resetForm = () => {
  setFormData({
    bank: null,
    accountNumber: "",
    receiversName: "",
  });

  setErrors({
    bank: "",
    accountNumber: "",
  });
};

  const handleSave = () => {
    if (!isFormValid()) return;
   addBeneficiary({
     account_name: formData.receiversName,
     account_number: formData.accountNumber,
     bank_code: formData.bank?.code || "",
     bank_name: formData.bank?.name || "",
   })
     .unwrap()
     .then(() => {
       toast.success("Beneficiary saved successfully");
       resetForm(); 
     })
     .catch((err) => {
       toast.error(err?.data?.error?.message || "Failed to save beneficiary");
       console.error("❌ Failed to save beneficiary:", err);
     });
  };

  const handleEditClick = (account: any) => {
    // Find the matching bank from banks response
    const selectedBank = banks?.find(
      (bank: any) =>
        bank.code === account.bank_code || bank.name === account.bank_name
    );

    setFormData({
      bank: selectedBank
        ? { name: selectedBank.name, code: selectedBank.code }
        : { name: account.bank_name, code: account.bank_code },
      accountNumber: account.account_number,
      receiversName: account.account_name,
    });

    // (Optional) Scroll to the add/edit form for better UX
    const formSection = document.getElementById("payout-form");
    formSection?.scrollIntoView({ behavior: "smooth" });
  };

useEffect(() => {
    const bankCode = formData.bank?.code;
    const accountNumber = formData.accountNumber;

    if (bankCode && accountNumber && accountNumber.length === 10) {
      verifyAccount({ bankCode, accountNumber })
        .unwrap()
        .then((res) => {
          console.log("✅ Verified:", res);
          if (res?.data?.account_name) {
            setFormData((prev) => ({
              ...prev,
              receiversName: res.data.account_name,
            }));
          }
        })
        .catch((err) => {
          console.error("❌ Verification failed:", err);
          setFormData((prev) => ({
            ...prev,
            receiversName: "",
          }));
        });
    }
  }, [formData.bank?.code, formData.accountNumber]);

  return (
    <div className="p-6">
      {/* Linked Accounts Section */}
      <h3 className="text-[18px] font-semibold text-fleetdark mb-2">
        Linked Accounts List
      </h3>

      {loadingBeneficiaries ? (
        <p>Loading accounts...</p>
      ) : savedBeneficiaries.length === 0 ? (
        <p className="text-gray-500">No linked accounts yet.</p>
      ) : (
        <div className="space-y-3">
          {savedBeneficiaries.map((account) => (
            <div
              key={account.id}
              className="flex items-center justify-between border rounded-lg p-3 bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-medium text-fleetdark">
                    {account.account_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {account.bank_name} — {account.account_number}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="text-fleetblue hover:text-blue-700 transition"
                  onClick={() => handleEditClick(account)}
                >
                  <Pencil size={18} />
                </button>
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700 transition"
                  onClick={() => handleDeleteClick(account)}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Account Section */}
      <div className="mt-8">
        <h3 className="text-[18px] font-semibold text-fleetdark mb-2">
          Add New Payout Account
        </h3>
        {/* <p className="text-sm text-gray-500 mb-4">Dummy text</p> */}

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <BankDropdown
            banks={banks}
            isLoading={isLoading}
            isError={isError}
            bankData={{
              bankName: formData.bank?.name || "",
              bankCode: formData.bank?.code,
            }}
            handleInputChange={(e) => {
              const { name, value } = e.target;
              if (name === "bankData") {
                setFormData({
                  ...formData,
                  bank: { name: value.bankName, code: value.bankCode } as Bank,
                });
              }
            }}
            handleBlur={() => handleBlur("bank")}
            errors={{ bankName: errors.bank }}
            clearError={
              (field) => setErrors((prev) => ({ ...prev, [field]: undefined })) // ✅ clears instantly
            }
          />

          <div>
            <label className="block text-[16px] font-medium text-fleetdark mb-2">
              Account Number:<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={(e) => handleChange("accountNumber", e.target.value)}
              onBlur={() => handleBlur("accountNumber")}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
              placeholder="Enter account number"
            />
            {verifying && (
              <p className="text-blue-500 text-sm mt-1">Verifying account...</p>
            )}

            {verifyError && (
              <p className="text-red-500 text-sm mt-1">
                Failed to verify account. Please check details.
              </p>
            )}

            {errors.accountNumber && (
              <p className="text-red-500 text-sm mt-1">
                {errors.accountNumber}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-[16px] font-medium text-fleetdark mb-2">
            Receiver's Name:
          </label>
          <input
            type="text"
            name="receiversName"
            value={formData.receiversName}
            className="w-full px-4 py-3 border bg-gray-50 border-gray-300 rounded-lg outline-none"
            readOnly
          />
        </div>

        <div className="mt-6">
          <button
            className={`w-auto py-3 px-6 rounded-lg ${
              isFormValid()
                ? "bg-fleetblue text-white"
                : "bg-[#B4C0D8] text-[#D0D7E7]"
            }`}
            disabled={!isFormValid()}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <FleetrifyModal
        isOpen={showDeleteModal}
        title="Delete Linked Account"
        onClose={() => setShowDeleteModal(false)}
        className="max-w-md h-fit"
      >
        <div className="space-y-4 p-6">
          <p className="text-fleetblue">
            Are you sure you want to delete{" "}
            <strong>{selectedAccount?.name}</strong> from your linked account
            list?
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 rounded-lg bg-red-600 text-white"
            >
              Delete Account
            </button>
          </div>
        </div>
      </FleetrifyModal>
    </div>
  );
};

export default ManagePayoutAccounts;
